import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type {
  FilterOptionType,
  ForecastProductListParamsType,
} from '../../@types/forecastFilterTypes'
import { FORECAST_FILTER_STORAGE_KEY, INITIAL_FILTERS } from '../../constants/demandPlanning'
import { loadFiltersFromStorage } from '../../helper/forecastFilter'
import {
  fetchBrandOptions,
  fetchHubOptions,
  fetchProductTypeOptions,
  fetchCategoryL1Options,
  fetchCategoryL2Options,
  fetchPICOptions,
  fetchBusinessLineOptions,
  fetchHardcodeReasonOptions,
  fetchSourceOptions,
  fetchParetoOptions,
  fetchCampaignTypeOptions,
  fetchVendorOptions,
  fetchPaydayPeriodOptions,
  fetchCategoryDemandOptions,
  fetchCampaignStatusOptions,
  fetchFCStatusOptions,
  fetchProductSuggestions,
} from './forecastFilterThunk'
import { mergeProductSuggestions } from '../../helper/productSuggestion'

const ERROR_MSG = 'An error occurred'

export type SavedSelectedOptionsType = {
  l1: FilterOptionType[]
  l2: FilterOptionType[]
  brand: FilterOptionType[]
  pic: FilterOptionType[]
  hub: FilterOptionType[]
  vendor: FilterOptionType[]
}

export interface ForecastFilterStateType {
  isLoadingBrands: boolean
  isLoadingHubs: boolean
  isLoadingProductTypes: boolean
  isLoadingCategories: boolean
  isLoadingPIC: boolean
  isLoadingBusinessLines: boolean
  isLoadingHardcodeReasons: boolean
  isLoadingSource: boolean
  isLoadingStorageHandling: boolean
  isLoadingPareto: boolean
  isLoadingProductSuggestions: boolean

  brandOptions: FilterOptionType[]
  hubOptions: FilterOptionType[]
  productTypeOptions: FilterOptionType[]
  categoryL1Options: FilterOptionType[]
  categoryL2FlatOptions: FilterOptionType[]
  picOptions: FilterOptionType[]
  businessLineOptions: FilterOptionType[]
  hardcodeReasonOptions: FilterOptionType[]
  sourceOptions: FilterOptionType[]
  paretoOptions: FilterOptionType[]
  campaignTypeOptions: FilterOptionType[]
  vendorOptions: FilterOptionType[]
  paydayPeriodOptions: FilterOptionType[]
  categoryDemandOptions: FilterOptionType[]
  campaignStatusOptions: FilterOptionType[]
  fcStatusOptions: FilterOptionType[]
  productSuggestions: FilterOptionType[]
  productSuggestionsNextCursor: string
  productSuggestionsRequestId: string | null

  savedSelectedOptions: SavedSelectedOptionsType | null

  selectedFilters: ForecastProductListParamsType
  appliedFilters: ForecastProductListParamsType

  error: string | null
}

const { filters: storedFilters, savedOptions: storedSavedOptions } = loadFiltersFromStorage(
  FORECAST_FILTER_STORAGE_KEY,
)
const hydratedFilters: ForecastProductListParamsType = { ...INITIAL_FILTERS, ...storedFilters }

const initialState: ForecastFilterStateType = {
  isLoadingBrands: false,
  isLoadingHubs: false,
  isLoadingProductTypes: false,
  isLoadingCategories: false,
  isLoadingPIC: false,
  isLoadingBusinessLines: false,
  isLoadingHardcodeReasons: false,
  isLoadingSource: false,
  isLoadingStorageHandling: false,
  isLoadingPareto: false,
  isLoadingProductSuggestions: false,

  brandOptions: [],
  hubOptions: [],
  productTypeOptions: [],
  categoryL1Options: [],
  categoryL2FlatOptions: [],
  picOptions: [],
  businessLineOptions: [],
  hardcodeReasonOptions: [],
  sourceOptions: [],
  paretoOptions: [],
  campaignTypeOptions: [],
  vendorOptions: [],
  paydayPeriodOptions: [],
  categoryDemandOptions: [],
  campaignStatusOptions: [],
  fcStatusOptions: [],
  productSuggestions: [],
  productSuggestionsNextCursor: '',
  productSuggestionsRequestId: null,

  savedSelectedOptions: storedSavedOptions,

  selectedFilters: hydratedFilters,
  appliedFilters: hydratedFilters,

  error: null,
}

export const sliceName = 'forecastFilter'

const forecastFilterSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState,
    setSelectedFilters: (state, action: PayloadAction<Partial<ForecastProductListParamsType>>) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload }
    },
    clearSelectedFilters: (state) => {
      state.selectedFilters = INITIAL_FILTERS
      state.savedSelectedOptions = null
    },
    setAppliedFilters: (state, action: PayloadAction<ForecastProductListParamsType>) => {
      state.appliedFilters = action.payload
    },
    setSavedOptions: (state, action: PayloadAction<SavedSelectedOptionsType>) => {
      state.savedSelectedOptions = action.payload
    },
    clearProductSuggestions: (state) => {
      state.isLoadingProductSuggestions = false
      state.productSuggestions = []
      state.productSuggestionsNextCursor = ''
      state.productSuggestionsRequestId = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Brand Options
      .addCase(fetchBrandOptions.pending, (state) => {
        state.isLoadingBrands = true
        state.error = null
      })
      .addCase(fetchBrandOptions.fulfilled, (state, action) => {
        state.isLoadingBrands = false
        state.brandOptions = action.payload
      })
      .addCase(fetchBrandOptions.rejected, (state, action) => {
        state.isLoadingBrands = false
        state.error = action.error.message || ERROR_MSG
      })
      // Hub Options
      .addCase(fetchHubOptions.pending, (state) => {
        state.isLoadingHubs = true
        state.error = null
      })
      .addCase(fetchHubOptions.fulfilled, (state, action) => {
        state.isLoadingHubs = false
        state.hubOptions = action.payload
      })
      .addCase(fetchHubOptions.rejected, (state, action) => {
        state.isLoadingHubs = false
        state.error = action.error.message || ERROR_MSG
      })
      // Product Type Options
      .addCase(fetchProductTypeOptions.pending, (state) => {
        state.isLoadingProductTypes = true
        state.error = null
      })
      .addCase(fetchProductTypeOptions.fulfilled, (state, action) => {
        state.isLoadingProductTypes = false
        state.productTypeOptions = action.payload
      })
      .addCase(fetchProductTypeOptions.rejected, (state, action) => {
        state.isLoadingProductTypes = false
        state.error = action.error.message || ERROR_MSG
      })
      // Category L1 Options (independent)
      .addCase(fetchCategoryL1Options.pending, (state) => {
        state.isLoadingCategories = true
        state.error = null
      })
      .addCase(fetchCategoryL1Options.fulfilled, (state, action) => {
        state.isLoadingCategories = false
        state.categoryL1Options = action.payload
      })
      .addCase(fetchCategoryL1Options.rejected, (state, action) => {
        state.isLoadingCategories = false
        state.error = action.error.message || ERROR_MSG
      })
      // Category L2 Options (independent)
      .addCase(fetchCategoryL2Options.pending, (state) => {
        state.isLoadingCategories = true
        state.error = null
      })
      .addCase(fetchCategoryL2Options.fulfilled, (state, action) => {
        state.isLoadingCategories = false
        state.categoryL2FlatOptions = action.payload
      })
      .addCase(fetchCategoryL2Options.rejected, (state, action) => {
        state.isLoadingCategories = false
        state.error = action.error.message || ERROR_MSG
      })
      // PIC Options
      .addCase(fetchPICOptions.pending, (state) => {
        state.isLoadingPIC = true
        state.error = null
      })
      .addCase(fetchPICOptions.fulfilled, (state, action) => {
        state.isLoadingPIC = false
        state.picOptions = action.payload
      })
      .addCase(fetchPICOptions.rejected, (state, action) => {
        state.isLoadingPIC = false
        state.error = action.error.message || ERROR_MSG
      })
      // Business Line Options
      .addCase(fetchBusinessLineOptions.pending, (state) => {
        state.isLoadingBusinessLines = true
        state.error = null
      })
      .addCase(fetchBusinessLineOptions.fulfilled, (state, action) => {
        state.isLoadingBusinessLines = false
        state.businessLineOptions = action.payload
      })
      .addCase(fetchBusinessLineOptions.rejected, (state, action) => {
        state.isLoadingBusinessLines = false
        state.error = action.error.message || ERROR_MSG
      })
      // Hardcode Reason Options
      .addCase(fetchHardcodeReasonOptions.pending, (state) => {
        state.isLoadingHardcodeReasons = true
        state.error = null
      })
      .addCase(fetchHardcodeReasonOptions.fulfilled, (state, action) => {
        state.isLoadingHardcodeReasons = false
        state.hardcodeReasonOptions = action.payload
      })
      .addCase(fetchHardcodeReasonOptions.rejected, (state, action) => {
        state.isLoadingHardcodeReasons = false
        state.error = action.error.message || ERROR_MSG
      })
      // Source Options
      .addCase(fetchSourceOptions.pending, (state) => {
        state.isLoadingSource = true
        state.error = null
      })
      .addCase(fetchSourceOptions.fulfilled, (state, action) => {
        state.isLoadingSource = false
        state.sourceOptions = action.payload
      })
      .addCase(fetchSourceOptions.rejected, (state, action) => {
        state.isLoadingSource = false
        state.error = action.error.message || ERROR_MSG
      })
      // Pareto Options
      .addCase(fetchParetoOptions.pending, (state) => {
        state.isLoadingPareto = true
        state.error = null
      })
      .addCase(fetchParetoOptions.fulfilled, (state, action) => {
        state.isLoadingPareto = false
        state.paretoOptions = action.payload
      })
      .addCase(fetchParetoOptions.rejected, (state, action) => {
        state.isLoadingPareto = false
        state.error = action.error.message || ERROR_MSG
      })
      .addCase(fetchCampaignTypeOptions.fulfilled, (state, action) => {
        state.campaignTypeOptions = action.payload
      })
      .addCase(fetchVendorOptions.fulfilled, (state, action) => {
        state.vendorOptions = action.payload
      })
      .addCase(fetchPaydayPeriodOptions.fulfilled, (state, action) => {
        state.paydayPeriodOptions = action.payload
      })
      .addCase(fetchCategoryDemandOptions.fulfilled, (state, action) => {
        state.categoryDemandOptions = action.payload
      })
      .addCase(fetchCampaignStatusOptions.fulfilled, (state, action) => {
        state.campaignStatusOptions = action.payload
      })
      .addCase(fetchFCStatusOptions.fulfilled, (state, action) => {
        state.fcStatusOptions = action.payload
      })
      .addCase(fetchProductSuggestions.pending, (state, action) => {
        state.isLoadingProductSuggestions = true
        state.productSuggestionsRequestId = action.meta.requestId
        state.error = null
      })
      .addCase(fetchProductSuggestions.fulfilled, (state, action) => {
        if (state.productSuggestionsRequestId !== action.meta.requestId) return
        state.isLoadingProductSuggestions = false
        const suggestions = action.payload.suggestions.map((item) => ({
          label: item.value,
          value: item.key,
        }))
        state.productSuggestions = action.payload.append
          ? mergeProductSuggestions(state.productSuggestions, suggestions)
          : suggestions
        state.productSuggestionsNextCursor = action.payload.nextCursor
        state.productSuggestionsRequestId = null
      })
      .addCase(fetchProductSuggestions.rejected, (state, action) => {
        if (state.productSuggestionsRequestId !== action.meta.requestId) return
        state.isLoadingProductSuggestions = false
        state.productSuggestionsRequestId = null
        state.error = action.error.message || ERROR_MSG
      })
  },
})

export const {
  reset,
  setSelectedFilters,
  clearSelectedFilters,
  setAppliedFilters,
  setSavedOptions,
  clearProductSuggestions,
} = forecastFilterSlice.actions

export default forecastFilterSlice.reducer
